import { useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateTimetableTemplate, parseTimetableWorkbook } from '@timetable/utils';
import { useAuthStore } from '@/store/authStore';
import type { ParsedClass, TimetableRow, MasterDataValidationReport, ValidationError } from '@timetable/types';
import type { WizardState, WizardAction } from '../ImportWizard';

interface Step5Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  onNext: (patch?: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
}

interface ClassUploadEntry {
  cls: ParsedClass;
  file: File | null;
  rows: TimetableRow[];
  errors: ValidationError[];
  parsing: boolean;
}

export function Step5TimetableUpload({ state, dispatch, onNext, onBack }: Step5Props) {
  const { schoolContext } = useAuthStore();
  const singleFileRef = useRef<HTMLInputElement>(null);
  const perClassRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Per-class upload state (only used in per-class mode)
  const [classEntries, setClassEntries] = useState<ClassUploadEntry[]>(() =>
    (state.parsedMasterData?.classes ?? []).map((cls) => ({
      cls, file: null, rows: [], errors: [], parsing: false,
    }))
  );

  // Single-mode state
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const isPerClass = state.timetableFormat === 'per-class';
  const year = state.selectedAcademicYear || 'draft';
  const school = schoolContext ?? 'school';

  // ── Download template ──────────────────────────────────────────────────────

  const handleDownloadAll = () => {
    if (!state.parsedMasterData) return;
    const result = generateTimetableTemplate(state.parsedMasterData, isPerClass ? 'per-class' : 'single');
    const blobs = Array.isArray(result) ? result : [result];
    const classes = state.parsedMasterData.classes;
    blobs.forEach((blob, i) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cls = classes[i];
      a.download = isPerClass && cls
        ? `timetable-${cls.class_name}-${cls.section}-${year}.xlsx`
        : `timetable-${school}-${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const handleDownloadOne = (cls: ParsedClass) => {
    if (!state.parsedMasterData) return;
    const singleClassData = { ...state.parsedMasterData, classes: [cls] };
    const blob = generateTimetableTemplate(singleClassData, 'single') as Blob;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timetable-${cls.class_name}-${cls.section}-${year}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Per-class file upload ──────────────────────────────────────────────────

  const handlePerClassFile = async (cls: ParsedClass, file: File) => {
    if (!file.name.endsWith('.xlsx')) return;
    const key = `${cls.class_name}|${cls.section}`;

    setClassEntries((prev) =>
      prev.map((e) => e.cls === cls ? { ...e, parsing: true, file, rows: [], errors: [] } : e)
    );

    try {
      const { rows, errors } = await parseTimetableWorkbook(file, state.parsedMasterData!);
      setClassEntries((prev) =>
        prev.map((e) => e.cls === cls ? { ...e, parsing: false, rows, errors } : e)
      );
    } catch {
      setClassEntries((prev) =>
        prev.map((e) => e.cls === cls ? { ...e, parsing: false, errors: [{ row: 0, column: '', message: 'Failed to parse file' }] } : e)
      );
    }
  };

  const handleRemovePerClass = (cls: ParsedClass) => {
    setClassEntries((prev) =>
      prev.map((e) => e.cls === cls ? { ...e, file: null, rows: [], errors: [], parsing: false } : e)
    );
  };

  // ── Single-mode upload ─────────────────────────────────────────────────────

  const handleSingleFiles = async (files: File[]) => {
    if (!state.parsedMasterData) return;
    const xlsxFiles = files.filter((f) => f.name.endsWith('.xlsx'));
    if (xlsxFiles.length === 0) { setParseError('Only .xlsx files are accepted.'); return; }
    setParsing(true);
    setParseError(null);
    try {
      const allRows: TimetableRow[] = [];
      const allErrors: ValidationError[] = [];
      for (const file of xlsxFiles) {
        const { rows, errors } = await parseTimetableWorkbook(file, state.parsedMasterData);
        allRows.push(...rows);
        allErrors.push(...errors);
      }
      const validation: MasterDataValidationReport = { valid: allErrors.length === 0, errors: allErrors, warnings: [] };
      dispatch({ type: 'SET_TIMETABLE_UPLOAD', payload: { files: xlsxFiles, rows: allRows, validation } });
    } catch (err: unknown) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse file(s).');
    } finally {
      setParsing(false);
    }
  };

  // ── Commit per-class entries to wizard state ───────────────────────────────

  const commitPerClass = () => {
    const allRows = classEntries.flatMap((e) => e.rows);
    const allErrors = classEntries.flatMap((e) => e.errors);
    const allFiles = classEntries.map((e) => e.file).filter(Boolean) as File[];
    const validation: MasterDataValidationReport = { valid: allErrors.length === 0, errors: allErrors, warnings: [] };
    dispatch({ type: 'SET_TIMETABLE_UPLOAD', payload: { files: allFiles, rows: allRows, validation } });
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const allUploaded = isPerClass && classEntries.every((e) => e.file !== null);
  const anyErrors = isPerClass && classEntries.some((e) => e.errors.length > 0);
  const perClassReady = allUploaded && !anyErrors;

  const validation = state.timetableValidation;
  const canAdvance = isPerClass ? perClassReady : validation?.valid === true;

  const handleNext = async () => {
    if (isPerClass) commitPerClass();
    await onNext({ timetable_json: state.parsedTimetableRows, validation_status: 'valid' });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Step 5: Timetable Upload</Typography>

      <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadAll} disabled={!state.parsedMasterData}>
        {isPerClass ? 'Download All Class Templates' : 'Download Timetable Template'}
      </Button>

      {/* ── Per-class mode: one row per class ── */}
      {isPerClass ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Class</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Upload</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classEntries.map((entry) => {
                const key = `${entry.cls.class_name}|${entry.cls.section}`;
                const label = `${entry.cls.class_name} - ${entry.cls.section}`;
                return (
                  <TableRow key={key}>
                    <TableCell>{label}</TableCell>

                    {/* Download individual template */}
                    <TableCell>
                      <IconButton size="small" onClick={() => handleDownloadOne(entry.cls)} title="Download template">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </TableCell>

                    {/* Upload button */}
                    <TableCell>
                      <input
                        type="file"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        ref={(el) => { perClassRefs.current[key] = el; }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handlePerClassFile(entry.cls, f);
                          e.target.value = '';
                        }}
                      />
                      {entry.file ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>{entry.file.name}</Typography>
                          <IconButton size="small" onClick={() => handleRemovePerClass(entry.cls)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<UploadFileIcon />}
                          onClick={() => perClassRefs.current[key]?.click()}
                        >
                          Upload
                        </Button>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      {entry.parsing && <CircularProgress size={16} />}
                      {!entry.parsing && entry.file && entry.errors.length === 0 && (
                        <Chip icon={<CheckCircleIcon />} label={`${entry.rows.length} rows`} color="success" size="small" />
                      )}
                      {!entry.parsing && entry.errors.length > 0 && (
                        <Chip icon={<ErrorIcon />} label={`${entry.errors.length} error(s)`} color="error" size="small" />
                      )}
                      {!entry.parsing && !entry.file && (
                        <Chip label="Pending" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        /* ── Single mode: one drop zone ── */
        <Box
          onDrop={(e) => { e.preventDefault(); handleSingleFiles(Array.from(e.dataTransfer.files)); }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => singleFileRef.current?.click()}
          sx={{
            border: '2px dashed', borderColor: 'divider', borderRadius: 2,
            p: 4, textAlign: 'center', cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Drag &amp; drop your .xlsx file here, or click to browse
          </Typography>
          {state.timetableFiles.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
              {state.timetableFiles.map((f) => f.name).join(', ')}
            </Typography>
          )}
          <input ref={singleFileRef} type="file" accept=".xlsx" multiple style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files) handleSingleFiles(Array.from(e.target.files)); }} />
        </Box>
      )}

      {parsing && <CircularProgress size={24} />}
      {parseError && <Alert severity="error">{parseError}</Alert>}

      {/* Per-class error details */}
      {isPerClass && anyErrors && (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>Some files have errors. Fix them and re-upload.</Alert>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell><TableCell>Row</TableCell>
                  <TableCell>Column</TableCell><TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classEntries.flatMap((e) =>
                  e.errors.map((err, i) => (
                    <TableRow key={`${e.cls.class_name}-${i}`}>
                      <TableCell>{e.file?.name ?? e.cls.class_name}</TableCell>
                      <TableCell>{err.row}</TableCell>
                      <TableCell>{err.column}</TableCell>
                      <TableCell>{err.message}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {/* Single-mode error table */}
      {!isPerClass && validation && !validation.valid && (
        <Box>
          <Alert severity="error" sx={{ mb: 1 }}>{validation.errors.length} error(s) found.</Alert>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell><TableCell>Sheet</TableCell>
                  <TableCell>Row</TableCell><TableCell>Column</TableCell><TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validation.errors.map((err, i) => (
                  <TableRow key={i}>
                    <TableCell>{err.filename ?? '—'}</TableCell>
                    <TableCell>{err.sheet ?? '—'}</TableCell>
                    <TableCell>{err.row}</TableCell>
                    <TableCell>{err.column}</TableCell>
                    <TableCell>{err.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {!isPerClass && validation?.valid && (
        <Alert severity="success">
          Validated. {state.parsedTimetableRows?.length ?? 0} timetable rows ready.
        </Alert>
      )}

      {isPerClass && perClassReady && (
        <Alert severity="success">
          All {classEntries.length} classes uploaded. {classEntries.reduce((s, e) => s + e.rows.length, 0)} rows ready.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack}>Back</Button>
        <Button variant="contained" disabled={!canAdvance} onClick={handleNext}>Next</Button>
      </Box>
    </Box>
  );
}
